import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import { partirDescripcion } from '@/modules/cotizador/useCotizador'

import type { DatosHoja, LineaHoja } from './HojaCotizacion'

/**
 * DE DÓNDE SALEN LOS DATOS DE LA HOJA.
 *
 * Una cotización puede estar en dos sitios y la hoja tiene que salir igual en
 * los dos casos:
 *
 *   · LA QUE YA ESTÁ EN WORLD OFFICE tiene asiento, y sus renglones viven en
 *     el espejo. Es la de los documentos FEV que se le mandan al cliente.
 *   · LA QUE SE ARMÓ AQUÍ y todavía no ha viajado —desde la 0144 solo viaja
 *     cuando gerencia la aprueba— tiene sus renglones en `cotizacion_renglones`.
 *
 * Se prefiere siempre el asiento: si el documento existe allá, la hoja debe
 * decir lo que dice allá, porque es el que vale.
 *
 * Los datos de contacto salen del espejo de terceros, que desde la 0162 trae
 * dirección, teléfono, correo y ciudad. Nuestra matriz de clientes queda como
 * respaldo campo por campo. Si no están en ninguno de los dos, el renglón va
 * vacío: en un documento que ve el cliente, un dato de menos es mejor que uno
 * inventado.
 */

export type Fuente = {
  /** El documento de World Office, si ya existe. */
  idAsiento: number | null
  /** La cotización de la aplicación, para cuando todavía no ha viajado. */
  cotizacionId: string | null
  /** Lo que ya sabe quien abre la hoja, para no volver a pedirlo. */
  numero: string
  prefijo: string | null
  fecha: string | null
  cliente: string | null
  clienteDocumento: string | null
  vendedor: string | null
  /** El vendedor escogido al cotizar, para cuando todavía no hay documento. */
  idVendedor?: number | null
  /** La dirección de la obra: va cuando el cliente no tiene una registrada. */
  direccionObra?: string | null
  concepto: string | null
}

type FilaConcepto = {
  codigo: string | null
  concepto: string | null
  producto_completo: string | null
  cantidad: number | null
  unidad: string | null
  precio_unitario: number | null
  iva_tasa: number | null
  iva_valor: number | null
  total_linea: number | null
  subtotal: number | null
}

type FilaRenglon = {
  descripcion: string | null
  cantidad: number | null
  precio_unitario: number | null
  iva: number | null
  id_inventario: number | null
  unidad: string | null
}

type FilaEspejo = {
  direccion: string | null
  telefono: string | null
  email: string | null
  ciudad: string | null
}

type FilaCliente = {
  direccion: string | null
  telefono: string | null
  ciudad: string | null
  correo: string | null
}

export function useHojaCotizacion(fuente: Fuente | null) {
  return useQuery({
    queryKey: ['hoja-cotizacion', fuente?.idAsiento, fuente?.cotizacionId],
    enabled: fuente !== null,
    queryFn: () => armarHoja(fuente!),
  })
}

/**
 * La hoja de UN documento. Aparte del hook para que el consolidado de la obra
 * (useHojaDeLaObra) arme cada cotización exactamente igual que aquí.
 */
export async function armarHoja(f: Fuente): Promise<DatosHoja> {
  const lineas: LineaHoja[] = []

  /*
   * LOS TOTALES SE TRUNCAN, NO SE REDONDEAN.
   *
   * Comprobado contra la COT 2773 y contra el espejo: la base suma
   * 243.386,39 y el documento imprime 243.386; el total suma 289.629,80 y
   * el documento imprime 289.629, no 289.630. El IVA de cada unidad, en
   * cambio, sí se redondea —462 × 19 % = 87,78 y el papel dice 88—.
   *
   * Parecen dos criterios contradictorios y no lo son: uno es el importe
   * que se cobra y el otro es un dato de cada renglón. Se copian los dos
   * tal cual, porque el papel que manda es el que emite World Office.
   */
  let baseCruda = 0
  let ivaCrudo = 0

  if (f.idAsiento !== null) {
    const { data, error } = await supabase
      .from('vista_cotizacion_conceptos')
      .select('*')
      .eq('id_asiento', f.idAsiento)
      .order('linea')

    if (error) throw new Error(mensajeDeError(error))

    for (const c of (data ?? []) as unknown as FilaConcepto[]) {
      const cantidad = Number(c.cantidad ?? 0)
      const precio = Number(c.precio_unitario ?? 0)
      const tasa = c.iva_tasa === null ? null : Number(c.iva_tasa)

      lineas.push({
        codigo: c.codigo,
        descripcion: c.producto_completo ?? c.concepto ?? '',
        cantidad,
        unidad: c.unidad,
        precioUnitario: precio,
        ivaTasa: tasa,
        // EN ESTE DOCUMENTO EL IVA VA POR UNIDAD Y EL TOTAL VA SIN IVA.
        // Se comprobó contra la COT 2773: dos unidades a 100.601 dan
        // «Valor IVA 19.114» —el de UNA— y «Total 201.202», que es la base
        // sin impuesto. Poner ahí el total de la línea hace que los dos
        // papeles no cuadren, y el que manda es el de World Office.
        ivaValor: tasa === null ? 0 : Math.round(precio * tasa),
        total: Math.floor(Number(c.subtotal ?? cantidad * precio)),
      })

      // Para la caja del pie se usan los valores del espejo sin tocar: son
      // los que World Office calculó, con sus propios redondeos.
      baseCruda += Number(c.subtotal ?? cantidad * precio)
      ivaCrudo += Number(c.iva_valor ?? 0)
    }
  } else if (f.cotizacionId) {
    const { data, error } = await supabase
      .from('cotizacion_renglones')
      .select('*')
      .eq('cotizacion_id', f.cotizacionId)
      .order('orden')

    if (error) throw new Error(mensajeDeError(error))

    // EL CÓDIGO DE WORLD OFFICE, NO EL ID INTERNO. El renglón guarda el
    // nombre sin código (el cotizador lo parte); el código es lo que va antes
    // del « - » en el catálogo («IRL67 - REFLECTOR…»), como lo saca
    // vista_cotizacion_conceptos para las que ya están en WO. Imprimía 3326
    // (el id del inventario) en la COT-2026-0049.
    const filas = (data ?? []) as unknown as FilaRenglon[]
    const ids = [...new Set(filas.map((r) => r.id_inventario).filter((v): v is number => v !== null))]
    const codigos = new Map<number, string | null>()
    if (ids.length > 0) {
      const { data: cat } = await supabase
        .from('vista_catalogo')
        .select('id_inventario, descripcion')
        .in('id_inventario', ids)
      for (const p of (cat ?? []) as { id_inventario: number; descripcion: string | null }[]) {
        codigos.set(Number(p.id_inventario), partirDescripcion(p.descripcion ?? '').codigo)
      }
    }

    for (const r of filas) {
      const cantidad = Number(r.cantidad ?? 0)
      const precio = Number(r.precio_unitario ?? 0)
      const tasa = r.iva === null ? null : Number(r.iva)
      const base = cantidad * precio
      // Por unidad, como en el documento de World Office.
      const ivaValor = tasa === null ? 0 : Math.round(precio * tasa)

      lineas.push({
        codigo: r.id_inventario === null ? null : codigos.get(Number(r.id_inventario)) ?? null,
        descripcion: r.descripcion ?? '',
        cantidad,
        // LA QUE ESCOGIÓ EL TÉCNICO. Antes iba «Und.» fijo, y así una
        // cotización de 35 metros de cable se imprimía como 35 unidades.
        // Si el renglón no la trae, se cae a «Und.», que es lo que aplica
        // World Office cuando no se le manda ninguna.
        unidad: r.unidad ?? 'Und.',
        precioUnitario: precio,
        ivaTasa: tasa,
        ivaValor,
        total: Math.floor(base),
      })

      baseCruda += base
      ivaCrudo += ivaValor * cantidad
    }
  }

  /*
   * EL CONTACTO SALE DEL ESPEJO, NO DE NUESTRA MATRIZ.
   *
   * `clientes` es la matriz de la aplicación y se llena a mano: hoy tiene
   * dirección en 2 de 41. El espejo de World Office trae los 2.670 con su
   * dirección, teléfono, correo y ciudad (0162), y además es el mismo dato
   * que imprime el documento de allá, que es con el que este papel tiene
   * que coincidir.
   *
   * La matriz se queda como respaldo campo por campo: un cliente puede
   * tener aquí un teléfono que allá falta, y en un documento que va al
   * cliente vale más un dato de menos que uno inventado.
   */
  let espejo: FilaEspejo | null = null
  let matriz: FilaCliente | null = null

  if (f.clienteDocumento) {
    const [porEspejo, porMatriz] = await Promise.all([
      supabase
        .from('wo_clientes')
        .select('direccion, telefono, email, ciudad')
        .eq('identificacion', f.clienteDocumento)
        .maybeSingle(),
      supabase
        .from('clientes')
        .select('direccion, telefono, ciudad, correo')
        .eq('documento', f.clienteDocumento)
        .maybeSingle(),
    ])

    espejo = (porEspejo.data ?? null) as unknown as FilaEspejo | null
    matriz = (porMatriz.data ?? null) as unknown as FilaCliente | null
  }

  /*
   * EL VENDEDOR Y LA DIRECCIÓN, AUNQUE EL DOCUMENTO NO HAYA VIAJADO.
   *
   * Johan, sobre la COT-2026-0016 de Mario Aguirre: «¿por qué sale sin
   * vendedor, sin mail, sin ciudad?». El vendedor lo había escogido Daniel
   * al cotizar, pero la hoja solo lo leía del documento de World Office,
   * que todavía no existe. Y el cliente no tiene dirección registrada en
   * ningún lado, pero la obra sí: es la dirección de este trabajo, no un
   * dato inventado. Correo y ciudad no se rellenan: no los tiene nadie.
   */
  let vendedor = f.vendedor
  if (!vendedor && f.idVendedor) {
    const { data: v } = await supabase
      .from('wo_vendedores')
      .select('nombre')
      .eq('id_tercero', f.idVendedor)
      .maybeSingle()
    vendedor = (v as { nombre: string | null } | null)?.nombre ?? null
  }
  // 0308: «Automático» no guarda vendedor; el que le toca es el de quien la
  // elaboró, si es vendedor en World Office.
  if (!vendedor && f.cotizacionId && !f.idAsiento) {
    const { data: v } = await supabase.rpc('fn_vendedor_de_la_cotizacion', {
      p_cotizacion_id: f.cotizacionId,
    })
    vendedor = (v as string | null) ?? null
  }

  const contacto = {
    direccion: espejo?.direccion ?? matriz?.direccion ?? f.direccionObra ?? null,
    telefono: espejo?.telefono ?? matriz?.telefono ?? null,
    ciudad: espejo?.ciudad ?? matriz?.ciudad ?? null,
    correo: espejo?.email ?? matriz?.correo ?? null,
  }

  /*
   * EL TOTAL ES EL QUE SE FACTURA (OS-2026-0113).
   *
   * Truncar cuadraba con el papel de la COT 2773, pero World Office GUARDA el
   * total redondeado (289.630, no 289.629) y ese es el que pasa a la factura:
   * de 3.176 documentos, 1.201 cuadran redondeando y 494 truncando. El
   * consolidado de la OS-2026-0113 sumó 637.664 truncando la FEV 3076
   * (58.665,81 → 58.665) y en World Office son 58.666: «no dan los valores».
   * Con el documento allá se toma su total; sin él, se redondea. El IVA es lo
   * que falta para llegar, así la caja siempre suma.
   */
  let totalDeWo: number | null = null
  if (f.idAsiento !== null) {
    const { data: wo } = await supabase
      .from('vista_cotizaciones_world_office')
      .select('total_con_iva')
      .eq('id_asiento', f.idAsiento)
      .maybeSingle()
    const t = (wo as { total_con_iva: number | string | null } | null)?.total_con_iva
    totalDeWo = t === null || t === undefined ? null : Number(t)
  }
  const subtotal = Math.round(baseCruda)
  const total = totalDeWo ?? Math.round(baseCruda + ivaCrudo)
  const iva = total - subtotal

  return {
    numero: f.numero,
    prefijo: f.prefijo,
    fecha: f.fecha,
    cliente: f.cliente ?? 'Sin cliente',
    clienteDocumento: f.clienteDocumento,
    clienteDireccion: contacto.direccion,
    clienteTelefono: contacto.telefono,
    clienteCiudad: contacto.ciudad,
    clienteCorreo: contacto.correo,
    vendedor,
    concepto: f.concepto,
    lineas,
    subtotal,
    iva,
    total,
  }
}
